import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import AuthHeader from "@/components/AuthHeader";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) navigate(from, { replace: true });
    }, [user, from, navigate]);

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email.trim(), password);
            toast.success("Sessão iniciada");
            navigate(from, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Credenciais inválidas");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen relative overflow-hidden bg-primary text-white flex items-center justify-center px-6 pt-24 pb-12" data-testid="admin-login-page">
            <AuthHeader variant="dark" testId="admin-login-header" />
            <div aria-hidden className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }} />
            <div aria-hidden className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full opacity-15 pointer-events-none" style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }} />

            <div className="relative w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <Logo testId="admin-login-logo" size="lg" variant="white" />
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8 shadow-2xl">
                    <span className="eyebrow text-secondary">Painel administrativo</span>
                    <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight">Inicie a sua sessão</h1>
                    <p className="mt-2 text-sm text-white/70">Acesso restrito à equipa NOVIFOOD.</p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">Email</label>
                            <div className="mt-2 relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                                <input
                                    type="email" required autoFocus
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    data-testid="admin-login-email"
                                    className="w-full rounded-md border border-white/15 bg-white/10 pl-10 pr-3 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-secondary"
                                    placeholder="admin@novifood.pt"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">Password</label>
                            <div className="mt-2 relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                                <input
                                    type="password" required
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    data-testid="admin-login-password"
                                    className="w-full rounded-md border border-white/15 bg-white/10 pl-10 pr-3 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-secondary"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            type="submit" disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-secondary px-6 py-3.5 text-sm font-bold text-primary shadow-sm transition hover:-translate-y-0.5 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? "A entrar..." : "Entrar"} <ArrowRight className="h-4 w-4" />
                        </button>
                    </form>
                </div>
                <p className="mt-6 text-center text-xs text-white/50">Esqueceu a password? Contacte o administrador do sistema.</p>
            </div>
        </div>
    );
}
